// TypeScript twin of apps/druif/internal/catalogue/fold.go's Key(): the stored *_key columns and the
// FTS text are folded by the exporter, so a query must be folded by exactly the same rules or it
// would never match. fold.test.ts runs the shared vectors in apps/druif/internal/catalogue/testdata.
// Any rule change bumps FOLD_VERSION on both sides.

/** The fold rules this build implements; a catalogue with another fold_version is refused. */
export const FOLD_VERSION = 1;

/** Letters NFKD does not decompose, folded after lower-casing (final sigma joins plain sigma). */
const TRANSLITERATIONS: ReadonlyMap<string, string> = new Map([
  ["ß", "ss"],
  ["ø", "o"],
  ["đ", "d"],
  ["ł", "l"],
  ["æ", "ae"],
  ["œ", "oe"],
  ["þ", "th"],
  ["ς", "σ"],
]);

const MARK = /^\p{Mn}$/u;
const ALPHANUMERIC = /^[\p{L}\p{N}]$/u;

/**
 * Folds `text` into the form stored in every key column and FTS table: NFKD, marks dropped,
 * lower-cased, a few letters transliterated, every run of other characters collapsed to one space.
 */
export function foldKey(text: string): string {
  let out = "";
  let pendingSpace = false;
  const emit = (piece: string): void => {
    if (pendingSpace && out !== "") out += " ";
    pendingSpace = false;
    out += piece;
  };
  for (const char of text.normalize("NFKD")) {
    if (MARK.test(char)) continue;
    if (!ALPHANUMERIC.test(char)) {
      pendingSpace = true;
      continue;
    }
    const lower = char.toLowerCase();
    emit(TRANSLITERATIONS.get(lower) ?? lower);
  }
  return out;
}

/** The distinct overlapping three-character windows FTS5's trigram tokenizer indexes for `key`. */
export function trigrams(key: string): string[] {
  // Code points, not UTF-16 units: a trigram of a non-Latin name must not cut a character in two.
  const chars = Array.from(key);
  const seen = new Set<string>();
  for (let i = 0; i + 3 <= chars.length; i++) seen.add(chars.slice(i, i + 3).join(""));
  return [...seen];
}
